import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { io } from 'socket.io-client';
import { API_URL, refreshSession } from './api';
import { applyRealtime, REALTIME_EVENTS } from './realtime-cache';
import { session } from './session';

/** The part of a Socket.IO client this app uses (lets tests pass a fake). */
export interface RealtimeSocket {
  on(event: string, fn: (...args: any[]) => void): unknown; // eslint-disable-line @typescript-eslint/no-explicit-any
  disconnect(): unknown;
}
export type SocketFactory = (token: string) => RealtimeSocket;

const defaultFactory: SocketFactory = (token) =>
  io(API_URL, { auth: { token }, transports: ['websocket'], reconnectionDelayMax: 10_000 });

type Listener = (event: string, payload: unknown) => void;
interface RealtimeState {
  connected: boolean;
  addListener: (l: Listener) => () => void;
}
const RealtimeContext = createContext<RealtimeState>({ connected: false, addListener: () => () => undefined });

const subscribe = (cb: () => void) => session.subscribe(cb);
const getToken = () => session.get()?.accessToken ?? null;

/**
 * One staff socket for the whole app. Joins the `admin` room on connect, feeds every event into the query cache,
 * re-fetches live data on every (re)connect, and reconnects with the new token after a refresh.
 */
export function RealtimeProvider({ children, factory = defaultFactory }: { children: ReactNode; factory?: SocketFactory }) {
  const qc = useQueryClient();
  const token = useSyncExternalStore(subscribe, getToken, getToken);
  const [connected, setConnected] = useState(false);
  const listeners = useRef(new Set<Listener>());

  useEffect(() => {
    if (!token) return;
    const socket = factory(token);
    socket.on('ready', () => {
      setConnected(true);
      void qc.invalidateQueries({ queryKey: ['live'] });
      void qc.invalidateQueries({ queryKey: ['sos'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('session:error', (p: { code?: string }) => {
      setConnected(false);
      if (p?.code === 'UNAUTHORIZED') void refreshSession(token);
    });
    socket.on('session:ended', () => {
      setConnected(false);
      session.set(null);
    });
    for (const ev of REALTIME_EVENTS)
      socket.on(ev, (payload: unknown) => {
        applyRealtime(qc, ev, payload);
        for (const l of listeners.current) l(ev, payload);
      });
    return () => {
      socket.disconnect();
      setConnected(false);
    };
  }, [token, factory, qc]);

  const addListener = useCallback((l: Listener) => {
    listeners.current.add(l);
    return () => {
      listeners.current.delete(l);
    };
  }, []);
  const value = useMemo(() => ({ connected, addListener }), [connected, addListener]);
  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>;
}

export const useRealtime = () => useContext(RealtimeContext);

/** Run `fn` for every realtime event of the given names. */
export function useRealtimeEvent(events: string[], fn: (event: string, payload: unknown) => void) {
  const { addListener } = useRealtime();
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  const key = events.join(',');
  useEffect(
    () =>
      addListener((ev, p) => {
        if (key.split(',').includes(ev)) ref.current(ev, p);
      }),
    [addListener, key],
  );
}
